import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  useTheme,
} from '@chakra-ui/react';

interface AleaButtonChoiceProps {
  label: string;
  items: string[];
  value: string;
  onChange: (value: string) => void;
  errorMessage?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  labelFontWeight?: number;
  isRequired?: boolean;
}

const AleaButtonChoice: React.FC<AleaButtonChoiceProps> = ({
  label,
  items,
  value,
  onChange,
  errorMessage,
  disabled,
  fullWidth,
  labelFontWeight,
  isRequired,
}) => {
  const isInvalid = !!errorMessage;
  const theme = useTheme();

  const formLabelStyles = {
    fontWeight: labelFontWeight ?? (isRequired ? 500 : 300),
    marginBottom: '3px',
    color: 'alea.500',
    paddingLeft: '16px',
  };

  return (
    <FormControl isInvalid={isInvalid}>
      <FormLabel sx={formLabelStyles}>{label}</FormLabel>
      <Box
        bg="white"
        width={fullWidth ? '100%' : 'fit-content'}
      >
        <Flex
          wrap={fullWidth ? 'nowrap' : { base: 'wrap', md: 'nowrap' }}
          width={fullWidth ? '100%' : 'auto'}
        >
          {items.map((item, index) => {
            const isSelected = value === item;
            const isLast = index === items.length - 1;
            return (
              <Button
                key={item}
                onClick={() => !disabled && onChange(item)}
                variant="unstyled"
                h="50px"
                px="24px"
                flex={fullWidth ? '1 1 0' : { base: '0 0 auto', md: '1 1 0' }}
                minW={fullWidth ? 0 : undefined}
                w={fullWidth ? '100%' : 'auto'}
                textAlign="center"
                justifyContent="center"
                borderRadius={0}
                borderTopLeftRadius={index === 0 ? '24px' : 0}
                borderBottomLeftRadius={index === 0 ? '24px' : 0}
                borderTopRightRadius={isLast ? '24px' : 0}
                borderBottomRightRadius={isLast ? '24px' : 0}
                fontSize="18px"
                fontWeight={isSelected ? 600 : 400}
                color="gray.800"
                bg={isSelected ? 'white' : 'transparent'}
                border={isSelected ? '2px solid' : '1px solid'}
                borderColor={isSelected ? theme.colors.alea[500] : '#DCDCDC'}
                ml={index === 0 ? 0 : isSelected ? '-2px' : '-1px'}
                zIndex={isSelected ? 1 : 0}
                _hover={
                  isSelected
                    ? { bg: 'white' }
                    : { bg: 'gray.50' }
                }
                _active={{ bg: 'gray.100' }}
                isDisabled={disabled}
              >
                {item}
              </Button>
            );
          })}
        </Flex>
      </Box>
      {isInvalid && (
        <FormErrorMessage
          pl="16px"
          mt="3px"
          color="invalid.500"
          fontSize="15px"
          fontWeight={300}
        >
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default AleaButtonChoice;
